/**
 * Build a safe completion message for AI note generation.
 *
 * - Strips leaked template/structured-note markers so the note body is never
 *   dumped into the chat.
 * - Ensures a `<view_note id="...">` link only appears when a real note id is
 *   present, falling back to a generic completion message otherwise.
 */
export function buildCompletionMessage(
  fullMessage: string | null | undefined,
  noteId?: string | null,
): string {
  let message = typeof fullMessage === 'string' ? fullMessage : '';

  const templateMarkerIndex = message.indexOf('[[TEMPLATE JSON START]]');
  if (templateMarkerIndex !== -1) {
    message = message.slice(0, templateMarkerIndex).trim();
  }

  const noteMarkerIndex = message.indexOf('[[NOTE START]]');
  if (noteMarkerIndex !== -1) {
    message = message.slice(0, noteMarkerIndex).trim();
  }

  const viewNoteRegex = /<view_note\s+id=["']([^"']*)["'][^>]*>([^<]*)<\/view_note>/g;

  let hasValidLink = false;
  const sanitized = message.replace(viewNoteRegex, (match, id: string, text: string) => {
    if (id && id !== 'undefined' && id !== 'null' && id.trim() !== '') {
      hasValidLink = true;
      return match;
    }
    if (noteId) {
      hasValidLink = true;
      return `<view_note id="${noteId}">${text || 'View Note'}</view_note>`;
    }
    return 'Your note is ready.';
  });

  if (hasValidLink) {
    return sanitized;
  }

  if (noteId) {
    return `Your note is ready. <view_note id="${noteId}">View Note</view_note>`;
  }

  return 'Your note is ready.';
}
